import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { Lock } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog'
import { apiErrorCode } from '@/lib/api-client'
import { useBillingPlans, useBillingSummary, type QuotaKey } from '@/hooks/use-billing'
import { QUOTA_META } from '@/pages/billing/plans'

const QUOTA_CODE_TO_KEY: Record<string, QuotaKey> = {
  QUOTA_MEMBERS_EXCEEDED: 'members',
  QUOTA_OFFICIAL_CHANNELS_EXCEEDED: 'officialChannels',
  QUOTA_PERSONAL_CHANNELS_EXCEEDED: 'personalChannels',
}

/**
 * Hook dùng chung: gọi `handle(err)` trong `onError` của mọi hành động bị chặn theo hạn mức gói
 * (kết nối kênh mới, mời thành viên). Trả `true` nếu đã nhận diện và mở popup (người gọi khỏi cần
 * tự hiện toast nữa); `false` nếu không phải lỗi hạn mức — người gọi hiện toast như cũ.
 */
export function useQuotaExceededDialog() {
  const [quotaKey, setQuotaKey] = useState<QuotaKey | null>(null)
  const handle = (err: unknown): boolean => {
    const key = QUOTA_CODE_TO_KEY[apiErrorCode(err) ?? '']
    if (!key) return false
    setQuotaKey(key)
    return true
  }
  const node = (
    <QuotaExceededDialog quotaKey={quotaKey} open={!!quotaKey} onOpenChange={(o) => !o && setQuotaKey(null)} />
  )
  return { handle, node }
}

export function QuotaExceededDialog({
  quotaKey, open, onOpenChange,
}: {
  quotaKey: QuotaKey | null
  open: boolean
  onOpenChange: (open: boolean) => void
}) {
  const { t } = useTranslation('sharedMisc')
  const navigate = useNavigate()
  const summary = useBillingSummary()
  const catalog = useBillingPlans()

  const s = summary.data
  const cfg = catalog.data
  const plan = cfg && s && !s.wasDowngraded ? cfg.plans.find((p) => p.id === s.effectivePlan) : undefined
  const active = !!s?.expiresAt && new Date(s.expiresAt).getTime() > Date.now()
  // Gói trả phí còn hạn, mua addon được → đưa thẳng vào Mua thêm; còn lại (free/hết hạn/gói thoả thuận
  // riêng) → Nâng cấp gói (addon-page.tsx tự chặn + trỏ về đây nếu không đủ điều kiện, cùng quy tắc).
  const eligibleForAddon = !!plan && plan.purchasable && (plan.monthlyPrice ?? 0) > 0 && active
  const meta = quotaKey ? QUOTA_META[quotaKey] : null

  const goBuy = () => {
    onOpenChange(false)
    navigate(eligibleForAddon ? `/billing/addon?quota=${quotaKey}` : `/billing/upgrade?quota=${quotaKey}`)
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Lock className="size-4 text-destructive" />
            {t('quotaExceededDialog.title')}
          </DialogTitle>
          <DialogDescription>
            {meta
              ? t('quotaExceededDialog.descriptionWithQuota', { quota: meta.short })
              : t('quotaExceededDialog.description')}
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>{t('quotaExceededDialog.later')}</Button>
          <Button onClick={goBuy}>{eligibleForAddon ? t('quotaExceededDialog.buyMore') : t('quotaExceededDialog.upgrade')}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
